import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import Navbar from './Navbar'
import { useTranslation } from 'react-i18next'
import { Typography, CircularProgress } from '@mui/material'
import AppointmentForm from './AppointmentForm'
import moment from 'moment'
import useCompanyInfo from '../hooks/useCompanyInfo'
import useQuery from '../hooks/useQuery'
import Map from './Map'
import LineSplit from './LineSplit'
import ErrorPage from './ErrorPage'

export const Wrapper = styled.div`
  padding: 0 1rem 1rem;
  height: calc(100vh - 62px);
  overflow-y: auto;
  background-color: ${(props) => props.theme.palette.background.main};
`

export const Card = styled.div`
  box-shadow: 2px 6px 15px 0px rgb(69 65 78 / 10%);
  background-color: ${(props) => props.theme.palette.surface.main};
`

export const Content = styled.div`
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
`

const OperationHours = styled.div`
  display: flex;
  flex-direction: column;
`

const SpinnerWrapper = styled.div`
  width: 100%;
  margin-top: 3rem;
  display: flex;
  justify-content: center;
`

const ResourceDescription = styled.div`
  margin-bottom: 1rem;
`

function AppointmentPage() {
  const { t, i18n } = useTranslation()
  let query = useQuery()
  const reqParams = {
    company: query.get('company'),
    resource: query.get('resource'),
    name: query.get('name'),
    email: query.get('email'),
    date: query.get('date'),
  }

  if (!reqParams.company || !reqParams.resource) return <ErrorPage error={t('missingUrlParams')} />

  const { company, resource, loading, fetchError } = useCompanyInfo(reqParams)

  const filterExceptedDates = useCallback(
    (date) => {
      const convertedDates = resource?.operationExceptionDates?.map((d) =>
        moment(d).format('YYYY-MM-DD'),
      )
      return convertedDates.includes(moment(date).format('YYYY-MM-DD'))
    },
    [resource],
  )

  const datePickerProps = useMemo(
    () => ({
      minDate: new Date(resource?.startDate),
      maxDate: new Date(resource?.endDate),
      shouldDisableDate: filterExceptedDates,
    }),
    [resource],
  )

  const appointmentTypes = useMemo(
    () => resource?.appointmentTypes && Object.entries(resource.appointmentTypes),
    [resource],
  )

  if (fetchError) return <ErrorPage error={fetchError} />

  return !loading && company && resource ? (
    <>
      <Navbar company={company} />
      <Wrapper>
        <Typography variant="h5" component="h5" style={{ padding: '1rem' }}>
          {company.displayName[i18n.language]}
        </Typography>
        <Card>
          <Typography variant="h6" component="h6" style={{ padding: '1rem' }}>
            {resource.displayName[i18n.language]}
          </Typography>
          <Content>
            <ResourceDescription>
              <LineSplit
                string={resource.description[i18n.language]}
                regexSeparator={/<br\/>|\\n/}
              />
            </ResourceDescription>
            <Map resource={resource} />
            {resource.address && (
              <>
                <Typography
                  variant="subtitle1"
                  component="span"
                  style={{ fontFamily: 'Lato-Bold' }}
                >
                  {t('address')}
                </Typography>
                <LineSplit string={resource.address} separator="\n" />
              </>
            )}
            <Typography variant="subtitle1" component="span" style={{ fontFamily: 'Lato-Bold' }}>
              {t('operationHours')}
            </Typography>
            <OperationHours>
              {resource.operationHours.map((i, index) => (
                <Typography key={index} variant="body2" component="span">
                  {i.day}: {i.times}
                </Typography>
              ))}
            </OperationHours>
            <AppointmentForm
              appointmentTypes={appointmentTypes}
              datePickerProps={datePickerProps}
              reqParams={reqParams}
            />
          </Content>
        </Card>
      </Wrapper>
    </>
  ) : (
    <SpinnerWrapper>
      <CircularProgress />
    </SpinnerWrapper>
  )
}

export default AppointmentPage
